<template>
  <div class="dashboard-container">
    <!-- 筛选条件 -->
    <el-card shadow="never" class="filter-card">
      <el-form :model="searchForm" label-width="60px" @submit.prevent="handleQuery">
        <el-row :gutter="20">
          <!-- 企业 -->
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-form-item label="企业">
              <el-select
                v-model="searchForm.enterpriseId"
                placeholder="请选择企业"
                clearable
                filterable
                @change="handleEnterpriseChange"
                style="width: 100%"
              >
                <el-option
                  v-for="item in enterpriseList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- 站点 -->
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-form-item label="站点">
              <el-select
                v-model="searchForm.stationId"
                placeholder="请选择站点"
                clearable
                filterable
                :disabled="!searchForm.enterpriseId"
                style="width: 100%"
              >
                <el-option
                  v-for="item in stationOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- 操作按钮 -->
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-form-item label=" ">
              <el-button type="primary" @click="handleQuery">查询</el-button>
              <el-button @click="handleReset">重置</el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 统计卡片（两个平分宽度） -->
    <el-row :gutter="20" class="stat-cards">
      <el-col :xs="12" :sm="12">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-label">今日金额</div>
          <div class="stat-value">￥{{ formatMoney(statData.todayAmount) }}</div>
          <div class="stat-sub">支付笔数：{{ statData.todayCount }}</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="12">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-label">昨日金额</div>
          <div class="stat-value">￥{{ formatMoney(statData.yesterdayAmount) }}</div>
          <div class="stat-sub">
            日同比：
            <span :style="{ color: statData.dayCompare >= 0 ? '#67c23a' : '#f56c6c' }">
              {{ statData.dayCompare >= 0 ? '+' : '' }}{{ statData.dayCompare.toFixed(2) }}%
            </span>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 图表 & 排名 -->
    <el-row :gutter="20" class="chart-row">
      <el-col :xs="24" :lg="16">
        <el-card shadow="hover" class="chart-card">
          <template #header>
            <div class="card-header">
              <span>日实付趋势</span>
              <el-radio-group v-model="trendDays" size="small" @change="fetchTrend">
                <el-radio-button :value="7">7天</el-radio-button>
                <el-radio-button :value="14">14天</el-radio-button>
                <el-radio-button :value="30">30天</el-radio-button>
              </el-radio-group>
            </div>
          </template>
          <div ref="trendChartRef" style="width: 100%; height: 300px;"></div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="8">
        <el-card shadow="hover" class="rank-card">
          <template #header>
            <span>今日订单实付排名</span>
          </template>
          <el-table :data="rankData" stripe style="width: 100%;" size="small">
            <el-table-column prop="stationName" label="站点" min-width="120" show-overflow-tooltip />
            <el-table-column prop="amount" label="实付金额" align="right" width="100">
              <template #default="{ row }">￥{{ formatMoney(row.amount) }}</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { pub } from '/common/event';
import * as echarts from 'echarts';
import enterpriseStationMixin from '@/mixins/enterpriseStationMixin';

export default {
  name: 'Dashboard',
  mixins: [enterpriseStationMixin],
  data() {
    return {
      searchForm: { enterpriseId: null, stationId: null },
      trendDays: 7,
      statData: {
        todayAmount: 0,
        todayCount: 0,
        yesterdayAmount: 0,
        dayCompare: 0,
      },
      rankData: [],
      trendChart: null,
      trendChartData: { dates: [], amounts: [] },
    };
  },
  computed: {
    userInfo() {
      return this.$store.state.user?.user || {};
    },
  },
  mounted() {
    this.initChart();
    this.fetchAll();
    window.addEventListener('resize', this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart);
    this.trendChart?.dispose();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resizeChart);
    if (this.trendChart) {
      this.trendChart.dispose();
      this.trendChart = null;
    }
  },
  methods: {
    // ✅ 新增：安全格式化金额
    formatMoney(value) {
      const num = Number(value);
      return isNaN(num) ? '0.00' : num.toFixed(2);
    },

    async fetchAll() {
      await Promise.all([this.fetchStatistics(), this.fetchTrend(), this.fetchRank()]);
    },
    async fetchStatistics() {
      try {
        const ret = await this.$r.post('order/order/statistics', this.searchForm);
        if (ret?.success) {
          this.statData = ret.data;
        }
      } catch (e) {
        console.error('获取统计数据失败', e);
      }
    },
    async fetchTrend() {
      try {
        const ret = await this.$r.post('order/order/trend', {
          ...this.searchForm,
          days: this.trendDays
        });
        if (ret?.success) {
          this.trendChartData = ret.data;
          this.updateChart();
        }
      } catch (e) {
        console.error('获取趋势数据失败', e);
      }
    },
    async fetchRank() {
      try {
        const ret = await this.$r.post('order/order/rank', this.searchForm);
        if (ret?.success) {
          // ✅ 确保 amount 为数字
          this.rankData = (ret.data || []).map(item => ({
            ...item,
            amount: Number(item.amount) || 0,
          }));
        }
      } catch (e) {
        console.error('获取排名数据失败', e);
      }
    },
    handleQuery() {
      this.fetchAll();
    },
    handleReset() {
      this.searchForm = { enterpriseId: null, stationId: null };
      this.stationOptions = [];
      this.fetchAll();
    },
    initChart() {
      this.trendChart = echarts.init(this.$refs.trendChartRef);
      this.updateChart();
    },
    updateChart() {
      const { dates, amounts } = this.trendChartData;
      const option = {
        tooltip: { trigger: 'axis' },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', data: dates, boundaryGap: false },
        yAxis: { type: 'value', axisLabel: { formatter: '￥{value}' } },
        series: [{
          name: '实付金额',
          type: 'line',
          smooth: true,
          data: amounts,
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
              { offset: 1, color: 'rgba(64, 158, 255, 0.05)' },
            ]),
          },
          lineStyle: { color: '#409eff' },
          itemStyle: { color: '#409eff' },
        }],
      };
      this.trendChart.setOption(option);
    },
    resizeChart() {
      this.trendChart?.resize();
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

.filter-card {
  margin-bottom: 24px;
  border-radius: 8px;
}
.filter-card ::v-deep .el-card__body {
  padding: 20px 24px 12px 24px;
}
.filter-card .el-form ::v-deep .el-form-item {
  margin-bottom: 12px;
}
.filter-card .el-form ::v-deep .el-form-item__label {
  line-height: 40px;
  font-weight: 500;
  color: #606266;
}
.filter-card .el-form ::v-deep .el-form-item__content {
  line-height: 40px;
}
.filter-card .el-form ::v-deep .el-input__inner {
  height: 40px;
  line-height: 40px;
}
.filter-card .el-form ::v-deep .el-button {
  height: 40px;
  padding: 0 24px;
}

.stat-cards {
  margin-bottom: 24px;
}
.stat-card {
  border-radius: 8px;
}
.stat-card ::v-deep .el-card__body {
  padding: 20px 24px;
}
.stat-card .stat-label {
  color: #909399;
  font-size: 14px;
}
.stat-card .stat-value {
  font-size: 28px;
  font-weight: 600;
  color: #303133;
  margin: 6px 0 4px 0;
}
.stat-card .stat-sub {
  color: #909399;
  font-size: 13px;
}

.chart-row {
  margin-bottom: 24px;
}
.chart-card,
.rank-card {
  border-radius: 8px;
}
.chart-card ::v-deep .el-card__header,
.rank-card ::v-deep .el-card__header {
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  font-size: 15px;
}
.chart-card ::v-deep .el-card__body,
.rank-card ::v-deep .el-card__body {
  padding: 16px 20px 20px 20px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.rank-card .el-table {
  margin-top: 0;
}
</style>